import React, { useEffect, useState } from "react";
import "../../pages/EditTablePage/styles.css";
import { showSuccess } from "../../utils/toast";
import { API_BASE_URL as API_URL } from '../../config/apiBase';


const SchemaEditTableModal = ({
  entityId,
  schemaId,
  onClose,
  onSuccess,
  isOpen,
  inline = false,
}) => {
  const [schema, setSchema] = useState(null);
  const [newFields, setNewFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const dataTypes = [
    { value: "text", label: "Text" },
    { value: "integer", label: "Number" },
    { value: "date", label: "Date" },
    { value: "time", label: "Time" },
    { value: "boolean", label: "Yes/No" },
    { value: "email", label: "Email" },
  ];

  useEffect(() => {
    if (schemaId && entityId && (isOpen || inline)) {
      fetchSchema();
    }
  }, [schemaId, entityId, isOpen]);

  const fetchSchema = async () => {
    if (!schemaId || !entityId) {
      setError("Schema ID and Entity ID are required");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const url = `${API_URL}/schemas/${schemaId}?entity_id=${entityId}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch schema");
      }

      const data = await response.json();
      setSchema(data.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addNewField = () => {
    setNewFields((prev) => [
      ...prev,
      { name: "", type: "text", required: false },
    ]);
  };

  const removeNewField = (index) => {
    setNewFields((prev) => prev.filter((_, i) => i !== index));
  };

  const updateNewField = (index, field, value) => {
    setNewFields((prev) => {
      const copy = [...prev];
      copy[index][field] = value;
      return copy;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (newFields.length === 0) {
      setError("Please add at least one new field");
      return;
    }

    for (let i = 0; i < newFields.length; i += 1) {
      if (!newFields[i].name.trim()) {
        setError(`Field ${i + 1} name is required`);
        return;
      }
    }

    const fieldNames = newFields.map((field) => field.name.toLowerCase());
    const uniqueNames = new Set(fieldNames);
    if (uniqueNames.size !== fieldNames.length) {
      setError("Field names must be unique");
      return;
    }

    const existingColumns = schema?.schema_definition?.columns || [];
    const existingFieldNames = existingColumns.map((column) =>
      column.name.toLowerCase()
    );
    for (const fieldName of fieldNames) {
      if (existingFieldNames.includes(fieldName)) {
        setError(`Field name '${fieldName}' already exists in the table`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/schemas/${schemaId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          new_fields: newFields.map((field) => ({
            name: field.name.trim(),
            type: field.type,
            required: field.required,
          })),
          entity_id: entityId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to update schema");
      }

      const msg = `Successfully added ${data.data.added_fields.length} new field(s) to the table`;
      setSuccess(msg);
      showSuccess(msg);
      setNewFields([]);
      await fetchSchema();
      onSuccess?.("fields", schemaId);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTable = async () => {
    if (!schema?.schema_id) return;
    setError("");
    setSuccess("");
    setDeleting(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/admin/entities/${entityId}/schemas/${schema.schema_id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to delete table");
      }
      const msg = "Table deleted successfully";
      setSuccess(msg);
      showSuccess(msg);
      setConfirmOpen(false);
      onSuccess?.("delete", schemaId);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const closeModal = () => {
    if (!submitting && !deleting) {
      onClose();
    }
  };

  if (!isOpen && !inline) {
    return null;
  }

  const existingColumns = schema?.schema_definition?.columns || [];

  const content = (
    <div
      className="admin-schema-modal-card"
      onClick={inline ? undefined : (event) => event.stopPropagation()}
    >
      {loading ? (
        <div className="schema-modal-skeleton">
          <div className="schema-modal-header">
            <div className="schema-skeleton-stack">
              <span
                className="schema-skeleton-pill"
                style={{ width: "110px" }}
              />
              <span
                className="schema-skeleton-line"
                style={{ width: "200px" }}
              />
              <span
                className="schema-skeleton-line subtle"
                style={{ width: "260px" }}
              />
            </div>
            <button
              type="button"
              className="schema-panel-close"
              onClick={closeModal}
            >
              ✕
            </button>
          </div>
          <div className="schema-modal-body">
            <div className="schema-skeleton-group">
              <span
                className="schema-skeleton-line"
                style={{ width: "160px" }}
              />
              {Array.from({ length: 3 }).map((_, idx) => (
                <div
                  key={`existing-skel-${idx}`}
                  className="schema-skeleton-field compact"
                >
                  <span
                    className="schema-skeleton-line"
                    style={{ width: "40%" }}
                  />
                  <span
                    className="schema-skeleton-line"
                    style={{ width: "30%" }}
                  />
                  <span
                    className="schema-skeleton-line subtle"
                    style={{ width: "20%" }}
                  />
                </div>
              ))}
            </div>
            <div className="schema-skeleton-group">
              <span
                className="schema-skeleton-line"
                style={{ width: "150px" }}
              />
              {Array.from({ length: 2 }).map((_, idx) => (
                <div
                  key={`new-field-skel-${idx}`}
                  className="schema-skeleton-field"
                >
                  <span
                    className="schema-skeleton-line"
                    style={{ width: "45%" }}
                  />
                  <span
                    className="schema-skeleton-line"
                    style={{ width: "35%" }}
                  />
                  <span
                    className="schema-skeleton-line subtle"
                    style={{ width: "20%" }}
                  />
                </div>
              ))}
            </div>
            <div className="schema-skeleton-actions">
              <span
                className="schema-skeleton-chip"
                style={{ width: "110px" }}
              />
              <span
                className="schema-skeleton-chip accent"
                style={{ width: "140px" }}
              />
            </div>
          </div>
        </div>
      ) : !schema ? (
        <div className="edit-table-error">
          <h3>Schema not found</h3>
          <p>The requested table schema could not be found.</p>
          <button
            type="button"
            className="back-to-tables-btn"
            onClick={closeModal}
          >
            Close
          </button>
        </div>
      ) : (
        <>
          <div className="schema-modal-header">
            <div>
              <p className="schema-panel-kicker">Edit Table</p>
              <h3>{schema.table_name}</h3>
              <p className="schema-panel-subtitle">
                Add new columns or delete the table
              </p>
            </div>
            <div className="schema-modal-footer">
              <button
                type="button"
                className="danger-outline-btn"
                onClick={() => setConfirmOpen(true)}
              >
                Delete Table
              </button>
            </div>
            {/* <button type="button" className="schema-panel-close" onClick={closeModal}>
                ✕
              </button> */}
          </div>

          <div className="schema-modal-body">
            <div className="existing-fields-section">
              <h4>Existing Fields</h4>
              <div className="table-info">
                <span className="version-info">Version {schema.version}</span>
                <span className="field-count">
                  {existingColumns.length} fields
                </span>
              </div>
              <div className="existing-fields-list">
                {existingColumns.map((column) => (
                  <div key={column.name} className="existing-field-item">
                    <div className="field-name">{column.name}</div>
                    <div className="field-type">{column.type}</div>
                    <div className="field-required">
                      {column.required ? "Required" : "Optional"}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="new-fields-section">
              <div className="section-header">
                <h4>Add New Fields</h4>
                <button
                  type="button"
                  className="add-field-btn"
                  onClick={addNewField}
                >
                  + Add New Field
                </button>
              </div>

              {newFields.length === 0 ? (
                <div className="no-new-fields-message">
                  <p>
                    No new fields added yet. Click &quot;Add New Field&quot; to
                    get started.
                  </p>
                </div>
              ) : (
                <div className="new-fields-list">
                  {newFields.map((field, index) => (
                    <div key={`new-field-${index}`} className="new-field-item">
                      <div className="field-inputs">
                        <div className="form-group">
                          <label>Field Name *</label>
                          <input
                            type="text"
                            value={field.name}
                            onChange={(event) =>
                              updateNewField(index, "name", event.target.value)
                            }
                            placeholder="Enter field name"
                            className="form-input"
                          />
                        </div>

                        <div className="form-group">
                          <label>Data Type *</label>
                          <select
                            value={field.type}
                            onChange={(event) =>
                              updateNewField(index, "type", event.target.value)
                            }
                            className="form-select"
                          >
                            {dataTypes.map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="form-group checkbox-group">
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={field.required}
                              onChange={(event) =>
                                updateNewField(
                                  index,
                                  "required",
                                  event.target.checked
                                )
                              }
                              className="checkbox-input"
                            />
                            <span className="checkbox-text">Required</span>
                          </label>
                        </div>

                        <button
                          type="button"
                          className="remove-field-btn"
                          onClick={() => removeNewField(index)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit} className="edit-table-form">
              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={submitting || newFields.length === 0}
                >
                  {submitting ? "Adding Fields..." : "Add Fields"}
                </button>
              </div>

              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}
            </form>
          </div>
        </>
      )}

      {confirmOpen && (
        <div
          className="modal-overlay"
          onClick={() => !deleting && setConfirmOpen(false)}
        >
          <div
            className="modal-card"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Delete table &quot;{schema?.table_name}&quot;</h3>
            </div>
            <div className="modal-body">
              <p className="modal-text">
                This action cannot be undone. This will permanently mark the
                table as deleted for this school.
              </p>
              <p className="modal-subtext">
                Please type{" "}
                <strong style={{ color: "#000", fontWeight: "bold" }}>
                  Delete {schema?.table_name}
                </strong>{" "}
                to confirm.
              </p>
              <input
                type="text"
                value={confirmText}
                onChange={(event) => setConfirmText(event.target.value)}
                placeholder={`Type Delete ${schema?.table_name} to confirm`}
                className={`confirm-input ${
                  confirmText === `Delete ${schema?.table_name}`
                    ? "valid"
                    : confirmText
                      ? "invalid"
                      : ""
                }`}
              />
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => setConfirmOpen(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="danger-btn"
                disabled={
                  deleting || confirmText !== `Delete ${schema?.table_name}`
                }
                onClick={handleDeleteTable}
              >
                {deleting ? "Deleting..." : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (inline) {
    return content;
  }

  return (
    <div className="admin-schema-modal-overlay" onClick={closeModal}>
      {content}
    </div>
  );
};

export default SchemaEditTableModal;
